<script>
import BaseModal from "@/javascript/views/components/BaseModal.vue";
import { VueDraggable } from "vue-draggable-plus";

const WIDGET_LABELS = {
  campaign_calendar: "Campaign Calendar",
  recent_gifts: "Recent Gifts",
  website_traffic: "Website Traffic",
  key_metrics: "Key Metrics",
  gift_frequency: "Gift Frequency",
  gift_type: "Gift Type",
  top_funds: "Top Funds",
  donation_funnel: "Donation Funnel",
  donor_retention: "Donor Retention",
  qr_scans: "QR Code Scans",
  community_map: "Community Map",
};

export default {
  name: "DashboardSettingsModal",
  components: { BaseModal, VueDraggable },
  props: {
    config: { type: Object, required: true },
  },
  emits: ["save", "close"],
  data() {
    return {
      localWidgets: [],
      isSaving: false,
    };
  },
  computed: {
    orderedWidgets: {
      get() {
        return this.localWidgets;
      },
      set(newOrder) {
        newOrder.forEach((w, i) => { w.position = i + 1; });
        this.localWidgets = newOrder;
      },
    },
  },
  created() {
    // Deep clone widgets excluding gifts_projection (pinned)
    this.localWidgets = JSON.parse(JSON.stringify(
      this.config.widgets.filter(w => w.key !== "gifts_projection")
    )).sort((a, b) => a.position - b.position);
  },
  methods: {
    label(key) {
      return WIDGET_LABELS[key] || key;
    },
    toggleVisibility(widget) {
      widget.visible = !widget.visible;
      // Re-sort: visible first, hidden last
      this.localWidgets.sort((a, b) => {
        if (a.visible === b.visible) return a.position - b.position;
        return a.visible ? -1 : 1;
      });
    },
    save() {
      this.isSaving = true;
      // Reconstruct full config with gifts_projection at position 0
      const giftsProjection = this.config.widgets.find(w => w.key === "gifts_projection");
      const widgets = [
        { ...giftsProjection },
        ...this.localWidgets.map((w, i) => ({ ...w, position: i + 1 })),
      ];
      this.$emit("save", { ...this.config, widgets });
    },
  },
};
</script>

<template>
  <BaseModal
    title="Edit Dashboard Layout"
    size="large"
    @close="$emit('close')"
  >
    <div class="phone-message">
      <i class="fa-solid fa-desktop text-gray-300 text-3xl mb-3" />
      <p class="text-gray-600 font-medium">
        Screen too small
      </p>
      <p class="text-gray-500 text-sm">
        Resize your browser to be at least 1000px wide to edit widgets.
      </p>
    </div>

    <div class="settings-content">
      <p class="text-sm text-gray-500 mb-4">
        Drag widgets to reorder. Toggle visibility with the eye icon.
      </p>

      <!-- Static Gifts chart placeholder -->
      <div class="gifts-placeholder">
        <i class="fa-solid fa-chart-line" />
        <span>Gifts Projection</span>
        <span class="gifts-hint">This widget is fixed and cannot be repositioned</span>
      </div>

      <VueDraggable
        v-model="orderedWidgets"
        :animation="150"
        handle=".drag-handle"
        class="widget-grid"
      >
        <div
          v-for="widget in orderedWidgets"
          :key="widget.key"
          :class="['widget-placeholder', `grid-${widget.size}`, { 'widget-disabled': !widget.visible }]"
        >
          <i class="fa-solid fa-grip-vertical drag-handle" />
          <span class="widget-label">{{ label(widget.key) }}</span>
          <button
            class="toggle-btn"
            @click="toggleVisibility(widget)"
          >
            <i :class="widget.visible ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'" />
          </button>
        </div>
      </VueDraggable>

      <div class="modal-footer">
        <button
          class="btn-save"
          :disabled="isSaving"
          @click="save"
        >
          <i
            v-if="isSaving"
            class="fa-solid fa-spinner fa-spin mr-1"
          />{{ isSaving ? "Saving..." : "Save" }}
        </button>
        <button
          class="btn-cancel"
          @click="$emit('close')"
        >
          Cancel
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<style lang="scss" scoped>
.widget-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 60px;
  grid-auto-flow: dense;
  gap: 12px;
  margin-bottom: 24px;
}

.gifts-placeholder {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.85rem;
  font-weight: 500;

  .gifts-hint {
    margin-left: auto;
    font-size: 0.7rem;
    font-weight: 400;
    color: #9ca3af;
    font-style: italic;
  }
}

.widget-placeholder {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  transition: opacity 0.2s, border-color 0.2s;

  &:hover { border-color: #10be5a; }
  &.widget-disabled { opacity: 0.4; }
}

.grid-1x1 { grid-column: span 1; grid-row: span 1; }
.grid-2x1 { grid-column: span 2; }
.grid-1x2 { grid-row: span 2; }
.grid-2x2 { grid-column: span 2; grid-row: span 2; }

.drag-handle {
  cursor: move;
  color: #9ca3af;
}

.widget-label {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
}

.toggle-btn {
  color: #6b7280;
  padding: 4px;
  border-radius: 4px;
  &:hover { color: #10be5a; background: #f0fdf4; }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.btn-cancel {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  border: 1px solid #d1d5db;
  &:hover { background: #f3f4f6; }
}

.btn-save {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  background: #08602d;
  color: white;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
// Responsiveness
.phone-message {
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

@media (max-width: 1000px) {
  .phone-message { display: flex; }
  .settings-content { display: none; }
}

@media (max-height: 768px) {
  .widget-grid {
    grid-auto-rows: 45px;
    gap: 8px;
    margin-bottom: 16px;
  }
  .gifts-placeholder {
    padding: 10px 14px;
    margin-bottom: 8px;
    font-size: 0.8rem;
  }
}
</style>
